import type { AwardCheck } from '@myrewards/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { letterKeys } from '../../api/letters';
import { checkKeys, taxEntryKeys } from '../../api/ops';
import { isStale, problemWords } from '../labels';

/**
 * A change to the check (cost type, tax, values taken again). The answer is the new check, which
 * replaces the cached one, so its version is the next one to send. A stale version reloads it.
 */
export function useCheckCommand<V>(
  awardId: string,
  run: (vars: V) => Promise<AwardCheck>,
  onDone?: (check: AwardCheck) => void,
) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: run,
    onSuccess: (check) => {
      setError(null);
      queryClient.setQueryData(checkKeys.one(awardId), check);
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
      // Taking the values again can clear each person's tax (APP-101 §8): read it again.
      void queryClient.invalidateQueries({ queryKey: taxEntryKeys.all });
      void queryClient.invalidateQueries({ queryKey: letterKeys.one(awardId) });
      onDone?.(check);
    },
    onError: (e) => {
      setError(problemWords(e));
      if (isStale(e)) void queryClient.invalidateQueries({ queryKey: checkKeys.one(awardId) });
    },
  });
  return {
    ...mutation,
    error,
    clearError: () => {
      setError(null);
    },
  };
}
